import creditCardType from 'credit-card-type'
import { Component, Vue } from 'vue-property-decorator'
import i18n from '../../../checkout/plugins/i18n'

import {
  email,
  isValidDate,
  minLength, required,
  validateCNPJ,
  validateCPF,
} from './validators'

interface Field extends Vue {
  hasError?: boolean
  resetValidation(): void
  validate(value?: unknown): boolean | Promise<boolean>
}

@Component
export default class ValidatorMixin extends Vue {
  $refs!: {
    [key: string]: Vue
  }

  private findFieldsRecursively(component: Vue, fields: Field[]) {
    for (const child of component.$children) {
      this.findFieldsRecursively(child, fields)

      if ('validate' in child && 'hasError' in child) {
        fields.push(child as Field)
      }
    }

    return fields
  }

  private get fields() {
    return this.findFieldsRecursively(this, [])
  }

  required(val: string) {
    const msg = i18n.t('message.required', navigator.language)

    if (typeof val === 'string') {
      return required(val.trim()) || msg
    }

    return required(val) || msg
  }

  email(val: string) {
    return email(val) || i18n.t('message.invalidEmail', navigator.language)
  }

  cpf(val: string) {
    return validateCPF(val) || i18n.t('message.invalidTaxId', navigator.language)
  }

  cnpj(val: string) {
    return validateCNPJ(val) || 'Informe um CNPJ válido'
  }

  minLength(length: number, msg: string) {
    return (val: string) => minLength(length, val) || msg
  }

  date(val: string) {
    return isValidDate(val) || i18n.t('message.invalidDate', navigator.language)
  }

  expirationDate(val: string) {
    const msg = i18n.t('message.invalidExpirationDate', navigator.language)
    const date = ['01/', val.slice(0, 3), '20', val.slice(3)].join('')

    if (!isValidDate(date)) {
      return msg
    }

    const [day, month, year] = date.split('/').map((part) => parseInt(part, 10))
    const expiry = new Date(year, (month - 1), day)
    const current = new Date()

    current.setDate(1)
    current.setHours(0, 0, 0, 0)

    return (expiry.getTime() >= current.getTime()) || msg
  }

  creditCard(val = '') {
    const cardNumber = val.replace(/\D/g, '')
    const [cardType] = creditCardType(cardNumber)
    const acceptedCards = ['visa', 'mastercard', 'hiper', 'elo', 'diners-club', 'hipercard', 'american-express']

    if (!cardType || !cardType.type) {
      return i18n.t('message.invalidCardNumber', navigator.language)
    }

    return acceptedCards.includes(cardType.type) || i18n.t('message.unacceptedCardNumber', navigator.language)
  }

  validateFields(): boolean {
    this.fields.forEach((field) => field.validate && field.validate())

    return this.fields.every((field) => !field.hasError)
  }

  resetValidation() {
    this.fields.forEach((field) => field.resetValidation && field.resetValidation())
  }
}
